import {
  ReactivateAccountCommand,
  ChangePasswordCommand,
  ActiveEntitiesQuery,
  ActiveEntitiesSummary,
  PipedriveComboCommand
} from './../apiclient/apiclient_generated';
import { Action, getModule, Module, VuexModule, Mutation } from 'vuex-module-decorators';
import store from '@/store/index';
import { WS_URL as ApiUrl } from '@/config';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import router from '@/router';
import Routes from '@/types/Routes';

import {
  AccountClient,
  ApplicationRoleType,
  AuthClient,
  AuthenticationResult,
  CreateAccountCommand,
  ExportAccountsCommand,
  IAuthenticationResult,
  LoginUserCommand,
  RefreshUserCommand,
  RegisterCustomerCommand,
  ServiceResultOfUserDto,
  ToolsClient,
  UpdateAccountCommand,
  PipedriveClient
} from '@/apiclient/apiclient_generated';
import { NotificationService } from '@/services/notification-service';

@Module({ dynamic: true, namespaced: true, store, name: 'account' })
export class AccountModule extends VuexModule {
  private toolsClient = new ToolsClient(ApiUrl, axios);
  private authClient = new AuthClient(ApiUrl, axios);
  private accountClient = new AccountClient(ApiUrl, axios);
  private pipedriveClient = new PipedriveClient(ApiUrl, axios);

  accountSearchValue = '';

  private _token = '_token';
  private _refreshToken = '_refreshToken';

  allAccounts = [];

  authData: IAuthenticationResult = new AuthenticationResult();

  get isAdmin() {
    return this.userData.role == ApplicationRoleType.Administrator;
  }

  get isCustomer() {
    return this.userData.role == ApplicationRoleType.Customer;
  }

  get isEmployee() {
    return this.userData.role == ApplicationRoleType.Employee;
  }

  get userData() {
    if (!this.authData.token) {
      return {};
    }

    const tokenData: any = jwt_decode(this.authData.token as string);

    const user: any = {
      id: tokenData.id,
      name: tokenData.name,
      surname: tokenData.surname,
      fullName: `${tokenData.name} ${tokenData.surname}`,
      email: tokenData.email,
      role: tokenData.account_role,
      company: tokenData.company
    };

    return user;
  }

  @Action
  async reactivateAcc(command: ReactivateAccountCommand) {
    const result = await this.accountClient.reactivateAccount(command);

    if (result.isSuccess && result.entity.id) {
      this.addAccount(result);
    }

    return result;
  }

  @Action
  async changePassword(command) {
    return await this.accountClient.changePassword(command);
  }

  @Action
  async resetPassword(command) {
    return await this.accountClient.resetPassword(command);
  }

  @Action
  async deleteAccountAction(id: string) {
    const result = await this.accountClient.deleteAccount(id);

    if (result.isSuccess) {
      this.processAccountDelete(id);
      NotificationService.NotifySuccess('Account was successfully removed.');
      return result.isSuccess;
    } else {
      NotificationService.NotifyErrors(result.errors);
    }
  }

  @Action
  async getAllUsers() {
    const allUsers = await this.accountClient.getAll();

    this.setAccounts(allUsers);
  }

  @Action
  async updateAccountAction(command: UpdateAccountCommand) {
    const result = await this.accountClient.updateAccount(command);

    if (result.isSuccess) {
      this.updateAccount(result);
    } else {
      NotificationService.NotifyErrors(result.errors);
    }

    return result;
  }

  @Action
  async exportAllUsers(command: ExportAccountsCommand) {
    return this.accountClient.export(command).then((data) => data);
  }

  @Action
  async doWork() {
    await this.toolsClient.migrateOldAccounts();
  }

  @Action
  async login(command: LoginUserCommand) {
    const data = await this.authClient.login(command);

    if (data.errorMessages?.length) {
      data.errorMessages.forEach((err) => {
        NotificationService.NotifyError(err);
      });
    }

    this.setAuthData(data);
    return data.isSuccess;
  }

  @Action
  async refresh() {
    const command = new RefreshUserCommand();
    command.refreshToken = this.authData.refreshToken;
    command.token = this.authData.token;

    this.setAuthData(this.authData);

    const refreshResult = await this.authClient.refresh(command);

    if (!refreshResult.isSuccess && refreshResult.errorMessages && refreshResult.errorMessages[0] === 'Invalid token') {
      this.logout();
      this.loginWithReturnUrl();
    } else {
      this.setAuthData(refreshResult);
    }
  }

  @Action
  async logout() {
    this.processLogout();
  }

  @Action
  async loginWithReturnUrl() {
    const returnRouteName: any = router.currentRoute.value.name;
    localStorage.setItem(Routes.ReturnUrl, returnRouteName);
    window.location.href = '/';
  }

  @Action
  async registerCustomer(command: RegisterCustomerCommand) {
    return await this.accountClient
      .registerCustomer(command)
      .then((data) => {
        return data;
      })
      .catch((error) => {
        return error;
      });
  }

  @Action
  async registerAccount(command: CreateAccountCommand) {
    return await this.accountClient
      .registerAccount(command)
      .then((data: any) => {
        if (data.isSuccess) {
          this.addAccount(data);
        }
        return data;
      })
      .catch((error) => {
        if (error.status === 400) {
          NotificationService.NotifyErrors(Object.values(error.response.errors));
        }
        return error;
      });
  }

  @Mutation
  setAuthData(result: IAuthenticationResult) {
    if (!result.token || !result.refreshToken) {
      localStorage.removeItem(this._token);
      localStorage.removeItem(this._refreshToken);
    } else {
      localStorage.setItem(this._token, result.token as string);
      localStorage.setItem(this._refreshToken, result.refreshToken as string);

      this.authData = result;
    }
  }

  @Mutation
  initAuthDataFromLocalStorage() {
    const token = localStorage.getItem(this._token) as string;

    if (token) {
      this.authData.token = token;
    }

    const refreshToken = localStorage.getItem(this._refreshToken) as string;
    if (refreshToken) {
      this.authData.refreshToken = refreshToken;
    }
  }

  @Mutation
  processLogout() {
    localStorage.removeItem(this._token);
    localStorage.removeItem(this._refreshToken);

    this.authData = {
      isSuccess: false
    };
  }

  @Mutation
  setAccounts(accounts) {
    accounts.forEach((acc) => {
      if (acc.createdDate) {
        acc.createdDate = new Date(acc.createdDate);
      }

      if (acc.modifiedDate) {
        acc.modifiedDate = new Date(acc.modifiedDate);
      }
    });

    this.allAccounts = accounts;
  }

  @Mutation
  updateAccount(updatedAccount: ServiceResultOfUserDto) {
    const index = this.allAccounts.findIndex((t) => t.id === updatedAccount.entity.id);

    if (index > -1) {
      const acc: any = updatedAccount.entity;
      if (acc.createdDate) {
        acc.createdDate = new Date(acc.createdDate);
      }
      if (acc.modifiedDate) {
        acc.modifiedDate = new Date(acc.modifiedDate);
      }
      this.allAccounts[index] = acc;
    }
  }

  @Mutation
  processAccountDelete(id) {
    const index = this.allAccounts.findIndex((t) => t.id === id);

    if (index > -1) {
      this.allAccounts.splice(index, 1);
    }
  }

  @Mutation
  addAccount(data) {
    const newAccount = data.entity;

    if (newAccount.createdDate) {
      newAccount.createdDate = new Date(newAccount.createdDate);
    }
    if (newAccount.modifiedDate) {
      newAccount.modifiedDate = new Date(newAccount.modifiedDate);
    }

    this.allAccounts.push(newAccount);
  }

  @Action
  async pushToPipedrive(command: PipedriveComboCommand) {
    return await this.pipedriveClient.pushToPipedrive(command);

    /*  return await this.pipedriveClient
      .pushToPipedrive(command)
      .then((data) => {
        if (data.isSuccess) {
          NotificationService.NotifySuccess('Account succesfully pushed to Pipedrive.');
        } else {
          NotificationService.NotifyErrors(data.errors);
        }
        return data;
      })
      .catch((error) => {
        if (error.status === 400) {
          NotificationService.NotifyErrors(Object.values(error.response.errors));
        }
        return error;
      }); */
  }

  @Mutation
  setAccountSearchValue(value) {
    this.accountSearchValue = value;
  }

  modifyAccountMode = false;
  @Mutation
  setModifyAccountMode(state: boolean) {
    this.modifyAccountMode = state;
  }
}

export const AccountStore = getModule(AccountModule);
